import { useCallback, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import type {
  EnrollmentState,
  EnrollmentSession,
  EnrollmentPrompt,
  EnrollmentResult,
  EnrollmentStatus,
} from '@/types/enrollment';
import type { BiometricFeatures, KeystrokeEvent } from '@/types/keystroke';
import { API_ENDPOINTS } from '@/types/api';

const REQUIRED_SESSIONS = 5;

/** Generate a device fingerprint from browser properties */
async function generateFingerprint(): Promise<string> {
  const data = [navigator.userAgent, screen.width, screen.height, navigator.language, new Date().getTimezoneOffset()].join('|');
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(data));
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
}

/** Default enrollment prompts in Portuguese */
const DEFAULT_PROMPTS: EnrollmentPrompt[] = [
  {
    id: 'p1',
    text: 'O sol nasceu devagar sobre as montanhas, iluminando o vale com tons dourados e alaranjados que refletiam na superficie calma do lago.',
    difficulty: 'easy',
    language: 'pt-BR',
    wordCount: 22,
  },
  {
    id: 'p2',
    text: 'A tecnologia avanca em ritmo acelerado, transformando completamente a maneira como nos comunicamos, trabalhamos e vivemos no mundo moderno.',
    difficulty: 'easy',
    language: 'pt-BR',
    wordCount: 20,
  },
  {
    id: 'p3',
    text: 'Cada pessoa possui um padrao unico de digitacao que funciona como uma impressao digital biometrica, impossivel de ser replicada com precisao.',
    difficulty: 'medium',
    language: 'pt-BR',
    wordCount: 20,
  },
  {
    id: 'p4',
    text: 'A seguranca digital depende de multiplas camadas de protecao, desde senhas fortes ate autenticacao biometrica avancada baseada em comportamento.',
    difficulty: 'medium',
    language: 'pt-BR',
    wordCount: 18,
  },
  {
    id: 'p5',
    text: 'Quando digitamos naturalmente sem pensar nas teclas individuais nosso cerebro cria padroes ritmicos que sao tao unicos quanto nossa caligrafia manuscrita.',
    difficulty: 'hard',
    language: 'pt-BR',
    wordCount: 20,
  },
];

interface UseEnrollmentReturn {
  /** Current enrollment state */
  enrollment: EnrollmentState;
  /** Current prompt to type */
  currentPrompt: EnrollmentPrompt | null;
  /** Whether a request is in progress */
  isLoading: boolean;
  /** Error message */
  error: string | null;
  /** Start enrollment process */
  startEnrollment: () => Promise<void>;
  /** Start a new typing session */
  startSession: () => void;
  /** Submit a completed session */
  submitSession: (
    events: KeystrokeEvent[],
    features: BiometricFeatures,
    typedText: string,
  ) => Promise<void>;
  /** Finalize enrollment and create profile */
  finalizeEnrollment: () => Promise<EnrollmentResult | null>;
  /** Reset enrollment */
  resetEnrollment: () => void;
  /** Get the prompt for the current session */
  getPrompt: (index: number) => EnrollmentPrompt;
}

function createEmptySession(index: number): EnrollmentSession {
  return {
    id: `session-${index}`,
    index,
    status: 'not_started',
    quality: null,
    qualityScore: 0,
    qualityChecks: [],
    features: null,
    text: '',
    typedText: '',
    wpm: 0,
    accuracy: 0,
    consistency: 0,
    duration: 0,
    startedAt: null,
    completedAt: null,
  };
}

function createInitialState(): EnrollmentState {
  return {
    status: 'not_started',
    totalSessions: REQUIRED_SESSIONS,
    completedSessions: 0,
    requiredSessions: REQUIRED_SESSIONS,
    currentSessionIndex: 0,
    sessions: Array.from({ length: REQUIRED_SESSIONS }, (_, i) => createEmptySession(i)),
    overallQuality: 0,
    profileReady: false,
    rhythmPrintGenerated: false,
  };
}

export function useEnrollment(): UseEnrollmentReturn {
  const [enrollment, setEnrollment] = useState<EnrollmentState>(createInitialState);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getPrompt = useCallback((index: number): EnrollmentPrompt => {
    return DEFAULT_PROMPTS[index % DEFAULT_PROMPTS.length];
  }, []);

  const currentPrompt = enrollment.currentSessionIndex < REQUIRED_SESSIONS
    ? getPrompt(enrollment.currentSessionIndex)
    : null;

  const startEnrollment = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const deviceContext = {
        device_fingerprint: await generateFingerprint(),
        keyboard_layout: 'abnt2',
        os: navigator.platform,
        browser: navigator.userAgent.split(' ').pop() || 'Unknown',
        user_agent: navigator.userAgent,
        screen_resolution: `${screen.width}x${screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
      };
      await apiClient.post(API_ENDPOINTS.ENROLLMENT_START, { device_context: deviceContext });

      setEnrollment({
        ...createInitialState(),
        status: 'in_progress',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start enrollment');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const startSession = useCallback(() => {
    setEnrollment((prev) => {
      const sessions = [...prev.sessions];
      const idx = prev.currentSessionIndex;
      if (idx < sessions.length) {
        sessions[idx] = {
          ...sessions[idx],
          status: 'collecting',
          text: getPrompt(idx).text,
          startedAt: new Date().toISOString(),
        };
      }
      return { ...prev, sessions, status: 'collecting' };
    });
  }, [getPrompt]);

  const submitSession = useCallback(
    async (
      events: KeystrokeEvent[],
      features: BiometricFeatures,
      typedText: string,
    ) => {
      setIsLoading(true);
      setError(null);

      try {
        const idx = enrollment.currentSessionIndex;
        const prompt = getPrompt(idx);

        // Determine quality
        const qualityChecks = [
          {
            name: 'wpm',
            label: 'Velocidade',
            passed: features.wpm >= 15,
            value: features.wpm,
            threshold: 15,
            unit: 'WPM',
          },
          {
            name: 'accuracy',
            label: 'Precisao',
            passed: features.accuracy >= 0.85,
            value: features.accuracy * 100,
            threshold: 85,
            unit: '%',
          },
          {
            name: 'consistency',
            label: 'Consistencia',
            passed: features.consistency >= 0.40,
            value: features.consistency * 100,
            threshold: 40,
            unit: '%',
          },
          {
            name: 'keystrokes',
            label: 'Teclas',
            passed: features.totalKeystrokes >= 20,
            value: features.totalKeystrokes,
            threshold: 20,
            unit: '',
          },
        ];

        const passedChecks = qualityChecks.filter((c) => c.passed).length;
        const qualityScore = (passedChecks / qualityChecks.length) * 100;

        let quality: EnrollmentSession['quality'];
        if (qualityScore >= 90) quality = 'excellent';
        else if (qualityScore >= 75) quality = 'good';
        else if (qualityScore >= 50) quality = 'acceptable';
        else if (qualityScore >= 25) quality = 'poor';
        else quality = 'rejected';

        // Submit to API
        const deviceContext = {
          device_fingerprint: await generateFingerprint(),
          keyboard_layout: 'abnt2',
          os: navigator.platform,
          browser: navigator.userAgent.split(' ').pop() || 'Unknown',
          user_agent: navigator.userAgent,
          screen_resolution: `${screen.width}x${screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          language: navigator.language,
        };
        await apiClient.post(API_ENDPOINTS.ENROLLMENT_SUBMIT, {
          features,
          device_context: deviceContext,
          typing_text: typedText,
          duration_ms: features.sessionDuration,
          wpm: features.wpm,
          accuracy: features.accuracy,
        });

        setEnrollment((prev) => {
          const sessions = [...prev.sessions];
          sessions[idx] = {
            ...sessions[idx],
            status: 'completed',
            quality,
            qualityScore,
            qualityChecks,
            features,
            typedText,
            wpm: features.wpm,
            accuracy: features.accuracy,
            consistency: features.consistency,
            duration: features.sessionDuration,
            completedAt: new Date().toISOString(),
          };

          const completedSessions = sessions.filter((s) => s.status === 'completed').length;
          const avgQuality =
            sessions
              .filter((s) => s.status === 'completed')
              .reduce((sum, s) => sum + s.qualityScore, 0) / Math.max(completedSessions, 1);

          return {
            ...prev,
            sessions,
            completedSessions,
            currentSessionIndex: completedSessions,
            overallQuality: avgQuality,
            status: completedSessions >= REQUIRED_SESSIONS ? 'processing' : 'in_progress',
            profileReady: completedSessions >= REQUIRED_SESSIONS,
          };
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to submit session');
      } finally {
        setIsLoading(false);
      }
    },
    [enrollment.currentSessionIndex, getPrompt],
  );

  const finalizeEnrollment = useCallback(async (): Promise<EnrollmentResult | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post<{
        success: boolean;
        template_id: string;
        confidence_score: number;
        message: string;
      }>(API_ENDPOINTS.ENROLLMENT_FINALIZE);

      setEnrollment((prev) => ({
        ...prev,
        status: 'completed',
        rhythmPrintGenerated: true,
      }));

      return {
        success: response.data.success,
        profileId: response.data.template_id,
        rhythmPrint: [], // Will be fetched from profile
        profileHealth: response.data.confidence_score * 100,
        message: response.data.message,
      };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to finalize enrollment');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetEnrollment = useCallback(() => {
    setEnrollment(createInitialState());
    setError(null);
  }, []);

  return {
    enrollment,
    currentPrompt,
    isLoading,
    error,
    startEnrollment,
    startSession,
    submitSession,
    finalizeEnrollment,
    resetEnrollment,
    getPrompt,
  };
}
